import React from 'react';
import { CustomStepProps } from '../../types/survey';

export const SleepAssessmentResults: React.FC<CustomStepProps> = ({
  onNext,
  onBack,
  surveyData,
}) => {
  // Calculate sleep assessment score based on user's answers
  const calculateSleepScore = () => {
    let totalScore = 0;
    const answers = surveyData?.answers || {};

    // Sleep hours scoring
    const sleepHours = answers.sleep_hours;
    if (sleepHours === 1) totalScore += 1; // Fewer than 4 hours
    else if (sleepHours === 2) totalScore += 2; // 4-6 hours
    else if (sleepHours === 3) totalScore += 4; // 7-8 hours
    else if (sleepHours === 4) totalScore += 2; // More than 8 hours

    // Sleep extremes scoring
    const sleepExtremes = answers.sleep_less_than_4_hours_or_more_than_10_hours;
    if (sleepExtremes === false) totalScore += 1; // No = 1 point
    // Yes = 0 points (no addition)

    // Sleep quality scoring (star rating, directly use the value)
    const sleepQuality = answers.sleep_quality;
    if (sleepQuality) totalScore += sleepQuality;

    // Fatigue frequency scoring
    const fatigue = answers.fatigue;
    if (fatigue === 1) totalScore += 1; // All the time
    else if (fatigue === 2) totalScore += 2; // Most of the time
    else if (fatigue === 3) totalScore += 3; // Sometimes
    else if (fatigue === 4) totalScore += 4; // Rarely
    else if (fatigue === 5) totalScore += 5; // Never

    // Severe fatigue scoring
    const severeFatigue = answers.fatigue_severe;
    if (severeFatigue === 1) totalScore += 5; // Never
    else if (severeFatigue === 2) totalScore += 4; // Rarely
    else if (severeFatigue === 3) totalScore += 3; // Sometimes
    else if (severeFatigue === 4) totalScore += 2; // Often
    else if (severeFatigue === 5) totalScore += 1; // Always

    return totalScore;
  };

  const score = calculateSleepScore();
  const hasProblems = score < 12;

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-blue-lightest via-white to-royal-gold-lightest flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/30 rounded-full mb-4">
                <svg className="w-10 h-10 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold mb-4 text-gray-800">
                Sleep Assessment Results
              </h1>
              <p className="text-xl text-gray-700">
                Your personalized sleep quality analysis
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Score Display */}
            <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-2xl p-6 mb-8">
              <div className="text-center">
                <div className="text-5xl font-bold mb-2 text-gray-800">{score}</div>
                <div className="text-lg text-gray-700">Total Score</div>
              </div>
            </div>

            {/* Summary Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Your Sleep Profile
              </h2>
              <div className={`${hasProblems ? 'bg-amber-50' : 'bg-green-50'} rounded-lg p-6`}>
                <p className={`text-gray-700 leading-relaxed ${hasProblems ? '' : 'text-green-900'}`}>
                  {hasProblems ? (
                    <>
                      Your results suggest that you have some sleep problems. Sleep is extremely important for many reasons. 
                      Problems with falling asleep and staying asleep directly affect your mental and physical health. 
                      Both too little and too much sleep affect the quality and length of your life. 
                      Do you want to learn more about sleep phases and get tested to rule out sleep apnea? 
                      Our specialists are at your disposal to improve the quality of your sleep.
                    </>
                  ) : (
                    <>
                      Good quality sleep is the cornerstone of a healthy, long life and it looks like you're taking proper care of it. 
                      Nothing provides as many benefits with as little effort as sleep! 
                      The quality and length of sleep directly affect mental and physical health, the nervous system, 
                      the cardiovascular system, the immune system and brain function. 
                      This in turn translates into physical endurance, emotional balance, creativity, vitality, and even body weight. 
                      Keep up your good habits. Well done!
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Expert Recommendations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Our Experts Recommend
              </h2>
              <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed">
                  Good quality sleep is essential for the body's regeneration; however, too much sleep does not always 
                  go hand in hand with feeling rested, and often indicates possible medical problems. 
                  Sleep is divided into certain phases and waking up in the wrong one can make you feel tired. 
                  The average cycle lasts 90 minutes, so waking up after 6, 7.5 or 9 hours should give good results. 
                  Do you want to learn more about sleep phases and get tested to rule out sleep apnea? 
                  Our specialists are at your disposal to improve the quality of your sleep.
                </p>
              </div>
            </div>

            {/* Score Breakdown */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Score Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Sleep Duration</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {(() => {
                        const sleepHours = surveyData?.answers?.sleep_hours;
                        if (sleepHours === 1) return '1 point';
                        if (sleepHours === 2) return '2 points';
                        if (sleepHours === 3) return '4 points';
                        if (sleepHours === 4) return '2 points';
                        return '0 points';
                      })()}
                    </span>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Sleep Quality</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {surveyData?.answers?.sleep_quality || 0} points
                    </span>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Fatigue Level</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {surveyData?.answers?.fatigue || 0} points
                    </span>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Sleep Consistency</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {surveyData?.answers?.sleep_less_than_4_hours_or_more_than_10_hours === false ? '1 point' : '0 points'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              <button
                onClick={onBack}
                className="px-8 py-3 bg-white border-2 border-royal-gold text-royal-gold font-semibold rounded-lg hover:bg-royal-gold hover:text-white transition-colors"
              >
                Previous
              </button>
              <button
                onClick={onNext}
                className="px-8 py-3 bg-royal-gold text-white font-semibold rounded-lg hover:bg-royal-gold-dark transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};